import {
  IconCalendarCog,
  IconCategory,
  IconDots,
  IconDownload,
  IconEdit,
  IconFilePencil,
  IconMail,
  IconMoneybag,
  IconPhone,
  IconReload,
  IconTrash,
  IconUserPlus,
} from '@tabler/icons-react';
import {
  getFullName,
  hasCustomClassCategories,
  logger,
  responseErrorToString,
} from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { brand } from '@zorro/web/design-system';
import {
  callEndpoint,
  exportEmployeePlanList,
  getApplicantsFromInsured,
  getClassDefinitionYear,
  getDefaultWhoIsEnrolled,
  showErrorNotification,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import {
  EnrollmentAction,
  getActionVisibility,
} from './EmployeePageActionsMenuUtils';

type Props = {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
};

export const EmployeePageActionsMenu = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  onEnrollmentTabActionChange,
}: Props) => {
  const [open, setOpen] = useState(false);

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: openEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const { data: customAttributes } = useMonolithQuery({
    method: 'allowanceModelControllerGetCustomAttributesForEmployerByYear',
    params: [
      employerId,
      getClassDefinitionYear(openEnrollmentPeriods, employerId),
    ],
  });

  const hasCustomClassCategory = hasCustomClassCategories(
    customAttributes ?? []
  );

  const { tryMutate: sendElectionWindowLaunchEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendElectionWindowLaunchEmail',
    successMessage: 'Email sent',
  });

  const { tryMutate: sendWaiverConfirmationEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendWaiverConfirmation',
    successMessage: 'Email sent',
  });

  const { tryMutate: sendElectionSummaryEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendSummaryEmail',
    successMessage: 'Email sent',
  });

  const { isEmployerAdmin, isZorroOperations } = useRoles();

  if (!onboardingPeriod || !employee) {
    return null;
  }

  const {
    withEditEnrollmentRecord,
    withAssignEnrollment,
    withDeleteEnrollmentRecord,
    withDownloadAllPlans,
    withChangeElectionWindow,
    withSendElectionWindowLaunchEmail,
    withSendWaiverConfirmationEmail,
    withSendElectionSummaryEmail,
    withChangeWaiverDetails,
    withResetElection,
    withEditAllowance,
    withLogEnrollmentCall,
  } = getActionVisibility({
    enrollmentStatus: onboardingPeriod.enrollmentStatus,
    isEmployerAdmin,
    isZorroOperations,
    onboardingUntil: onboardingPeriod.onboardingUntil,
  });

  const handleDownloadAllPlans = async () => {
    try {
      const insured = await callEndpoint({
        method: 'insuredControllerFindInsuredForPeriod',
        params: [onboardingPeriodId],
      });

      const benefits = await callEndpoint({
        method: 'benefitsControllerGetBenefits',
        params: [onboardingPeriodId],
      });

      const coveredInsured =
        benefits?.majorMedicalBenefit?.insuredPeople ?? insured;

      const insuredIds = getDefaultWhoIsEnrolled(coveredInsured);

      const applicants = getApplicantsFromInsured(
        employee?.email || '',
        coveredInsured,
        onboardingPeriod
      );

      const medicalPlans = await callEndpoint({
        method: 'majorMedicalControllerSearchMedicalPlansForOnboardingPeriod',
        params: [
          onboardingPeriodId,
          {
            applicants,
            insuredIds,
          },
        ],
      });

      exportEmployeePlanList(medicalPlans, getFullName(employee));
    } catch (error) {
      logger.error(error);
      showErrorNotification({ message: responseErrorToString(error) });
    }
  };

  return (
    <Menu opened={open} onChange={setOpen} size="md">
      <Menu.Target>
        <ActionIcon isChecked={open} bd={`1px solid ${brand.zorroGray300}`}>
          <IconDots color={brand.zorroCoal900} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Enrollment actions</Menu.Label>
        {withEditEnrollmentRecord && (
          <Menu.Item
            leftSection={<IconEdit size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(
                EnrollmentAction.EDIT_ENROLLMENT_RECORD
              )
            }
          >
            Edit enrollment record
          </Menu.Item>
        )}
        {withAssignEnrollment && (
          <Menu.Item
            leftSection={<IconUserPlus size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.ASSIGN_ENROLLMENT)
            }
          >
            Assign enrollment
          </Menu.Item>
        )}
        {withChangeElectionWindow && (
          <Menu.Item
            leftSection={<IconCalendarCog size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(
                EnrollmentAction.CHANGE_ELECTION_WINDOW
              )
            }
          >
            Change election window
          </Menu.Item>
        )}
        {withDownloadAllPlans && (
          <Menu.Item
            leftSection={<IconDownload size={16} stroke={1.25} />}
            onClick={handleDownloadAllPlans}
          >
            Download all plans
          </Menu.Item>
        )}
        {withChangeWaiverDetails && (
          <Menu.Item
            leftSection={<IconFilePencil size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(
                EnrollmentAction.CHANGE_WAIVER_DETAILS
              )
            }
          >
            Change waiver details
          </Menu.Item>
        )}
        {withEditAllowance && (
          <Menu.Item
            leftSection={<IconMoneybag size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.EDIT_ALLOWANCE)
            }
          >
            Edit allowance
          </Menu.Item>
        )}
        {hasCustomClassCategory && (
          <Menu.Item
            leftSection={<IconCategory size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(
                EnrollmentAction.CHANGE_CUSTOM_CLASS_CATEGORY
              )
            }
          >
            Change custom class category
          </Menu.Item>
        )}
        {withSendElectionWindowLaunchEmail && (
          <Menu.Item
            leftSection={<IconMail size={16} stroke={1.25} />}
            onClick={() => sendElectionWindowLaunchEmail([onboardingPeriodId])}
          >
            Send election window launch email
          </Menu.Item>
        )}
        {withSendWaiverConfirmationEmail && (
          <Menu.Item
            leftSection={<IconMail size={16} stroke={1.25} />}
            onClick={() => sendWaiverConfirmationEmail([employeeId])}
          >
            Send waiver confirmation email
          </Menu.Item>
        )}
        {withSendElectionSummaryEmail && (
          <Menu.Item
            leftSection={<IconMail size={16} stroke={1.25} />}
            onClick={() => sendElectionSummaryEmail([onboardingPeriodId])}
          >
            Send election summary email
          </Menu.Item>
        )}
        {withLogEnrollmentCall && (
          <Menu.Item
            leftSection={<IconPhone size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.LOG_ENROLLMENT_CALL)
            }
          >
            Log enrollment call
          </Menu.Item>
        )}
        {withResetElection && (
          <Menu.Item
            leftSection={<IconReload size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.RESET_ELECTION)
            }
          >
            Reset election
          </Menu.Item>
        )}
        {withDeleteEnrollmentRecord && (
          <Menu.Item
            leftSection={<IconTrash size={16} stroke={1.25} />}
            onClick={() =>
              onEnrollmentTabActionChange(
                EnrollmentAction.DELETE_ENROLLMENT_RECORD
              )
            }
          >
            Delete enrollment record
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
